import React, { createContext, useContext, useEffect, useState } from "react";

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const KEY = "ngx_cart";

// Items in cart are identified by product_id + variant_label (null for products without variants).
const itemKey = (i) => `${i.product_id}::${i.variant_label || ""}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  /**
   * Add a product (optionally with a chosen variant) to the cart.
   * @param product The product object from the API.
   * @param qty Quantity to add (default 1).
   * @param variant The selected variant `{label, price, stock}` — required if the
   *                product has variants, omitted otherwise.
   */
  const add = (product, qty = 1, variant = null) => {
    const newItem = {
      product_id: product.id,
      name: product.name,
      slug: product.slug,
      image_url: variant?.image_url || product.image_url,
      variant_label: variant?.label || null,
      price: variant ? Number(variant.price) : Number(product.price),
      quantity: qty,
    };
    setItems((prev) => {
      const k = itemKey(newItem);
      const found = prev.find((i) => itemKey(i) === k);
      if (found) {
        return prev.map((i) => itemKey(i) === k ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, newItem];
    });
    setOpen(true);
  };

  const remove = (key) => setItems((prev) => prev.filter((i) => itemKey(i) !== key));
  const setQty = (key, quantity) => {
    if (quantity < 1) return remove(key);
    setItems((prev) => prev.map((i) => itemKey(i) === key ? { ...i, quantity } : i));
  };
  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ items, add, remove, setQty, clear, subtotal, count, open, setOpen, itemKey }}>
      {children}
    </CartCtx.Provider>
  );
}
